import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { ErrorBanner, Modal } from "./ui";

/**
 * Shares a card template or one earn rule with chosen people, by mobile
 * number or email. What goes is how the card earns — never transactions,
 * balances, redemptions or card numbers.
 */
export function ShareModal({
  target,
  onClose,
  onShared,
}: {
  /** What to share, or null when closed. */
  target: { kind: "TEMPLATE" | "RULE"; sourceId: string; title: string } | null;
  onClose: () => void;
  onShared: (message: string) => void;
}) {
  const [people, setPeople] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPeople("");
    setNote("");
    setError(null);
  }, [target]);

  async function share(e: React.FormEvent) {
    e.preventDefault();
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sharedWith: string[]; notFound: string[] }>("/shares", {
        kind: target.kind,
        sourceId: target.sourceId,
        recipients: people.split(/[\n,;]+/).map((p) => p.trim()).filter(Boolean),
        note: note.trim() || null,
      });
      onShared(
        `Shared "${target.title}" with ${r.sharedWith.join(", ")}.` +
          (r.notFound.length > 0 ? ` No account was found for ${r.notFound.join(", ")}.` : ""),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not share it");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} title={`Share ${target?.kind === "RULE" ? "a rule" : "a card template"}`}>
      <form onSubmit={share} className="space-y-4">
        <p className="text-sm text-ink-700">
          <strong>{target?.title}</strong>
        </p>
        <div>
          <label className="label" htmlFor="sh-people">Share with</label>
          <textarea
            id="sh-people"
            className="input min-h-[5rem]"
            required
            value={people}
            onChange={(e) => setPeople(e.target.value)}
            placeholder={"98765 43210\npriya@example.com"}
          />
          <p className="mt-1 text-xs text-ink-500">
            Mobile numbers or emails, one per line or separated by commas. Each needs an account on the app.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="sh-note">A note (optional)</label>
          <input id="sh-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="How I've set this up for SmartBuy" />
        </div>
        <p className="rounded-lg bg-ink-50 p-3 text-xs text-ink-600">
          They see {target?.kind === "RULE" ? "the rule — what it earns on, its rate and cap —" : "the card's fees, rules, caps and milestones"}{" "}
          and your name. Never your transactions, balances, redemptions or card number. If you edit it later, they are
          offered the change; nothing on their cards changes unless they take it.
        </p>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Sharing…" : "Share"}</button>
        </div>
      </form>
    </Modal>
  );
}
